export type VersionBump = 'major' | 'minor' | 'patch';

export interface SemVer {
  major: number;
  minor: number;
  patch: number;
  prerelease?: string;
  build?: string;
}

export function parseSemVer(version: string): SemVer | null {
  // Regular expression for semantic versioning
  const semverRegex = /^(\d+)\.(\d+)\.(\d+)(?:-([a-zA-Z0-9\-.]+))?(?:\+([a-zA-Z0-9\-.]+))?$/;
  const match = version.match(semverRegex);
  
  if (!match) {
    return null;
  }
  
  return {
    major: parseInt(match[1], 10),
    minor: parseInt(match[2], 10),
    patch: parseInt(match[3], 10),
    prerelease: match[4],
    build: match[5],
  };
}

export function formatSemVer(semver: SemVer): string {
  let version = `${semver.major}.${semver.minor}.${semver.patch}`;
  
  if (semver.prerelease) {
    version += `-${semver.prerelease}`;
  }
  
  if (semver.build) {
    version += `+${semver.build}`;
  }
  
  return version;
}

export function bumpVersion(currentVersion: string, bumpType: VersionBump, prerelease?: string): string {
  const semver = parseSemVer(currentVersion);
  
  if (!semver) {
    throw new Error(`Invalid semantic version: ${currentVersion}`);
  }
  
  switch (bumpType) {
    case 'major':
      semver.major += 1;
      semver.minor = 0;
      semver.patch = 0;
      break;
    case 'minor':
      semver.minor += 1;
      semver.patch = 0;
      break;
    case 'patch':
      semver.patch += 1;
      break;
  }
  
  // Reset prerelease and build metadata when bumping
  semver.prerelease = prerelease;
  semver.build = undefined;
  
  return formatSemVer(semver);
}

export function getNextVersion(
  releases: Array<{ version: string; channel: string }>,
  channel: string,
  bumpType: VersionBump
): string {
  // Filter releases by channel
  const channelReleases = releases.filter(r => r.channel === channel);
  
  if (channelReleases.length === 0) {
    // No releases in this channel, start with 0.1.0
    return channel === 'stable' ? '1.0.0' : '0.1.0';
  }
  
  // Find the latest version in the channel
  const latestRelease = channelReleases.reduce((latest, current) => {
    const latestSemver = parseSemVer(latest.version);
    const currentSemver = parseSemVer(current.version);
    
    if (!latestSemver || !currentSemver) {
      return latest;
    }
    
    if (currentSemver.major > latestSemver.major) return current;
    if (currentSemver.major < latestSemver.major) return latest;
    
    if (currentSemver.minor > latestSemver.minor) return current;
    if (currentSemver.minor < latestSemver.minor) return latest;
    
    if (currentSemver.patch > latestSemver.patch) return current;
    
    return latest;
  });
  
  // Add prerelease suffix for non-stable channels
  const prerelease = channel !== 'stable' ? channel : undefined;
  
  return bumpVersion(latestRelease.version, bumpType, prerelease);
}

export function compareVersions(v1: string, v2: string): number {
  const semver1 = parseSemVer(v1);
  const semver2 = parseSemVer(v2);
  
  if (!semver1 || !semver2) {
    return v1.localeCompare(v2);
  }
  
  if (semver1.major !== semver2.major) {
    return semver1.major - semver2.major;
  }
  
  if (semver1.minor !== semver2.minor) {
    return semver1.minor - semver2.minor;
  }
  
  if (semver1.patch !== semver2.patch) {
    return semver1.patch - semver2.patch;
  }
  
  // If versions are equal, compare prerelease
  if (semver1.prerelease && !semver2.prerelease) {
    return -1; // v1 is prerelease, v2 is not
  }
  
  if (!semver1.prerelease && semver2.prerelease) {
    return 1; // v1 is not prerelease, v2 is
  }
  
  if (semver1.prerelease && semver2.prerelease) {
    return semver1.prerelease.localeCompare(semver2.prerelease);
  }
  
  return 0;
}